/**
 * Lightweight analytics façade — exports trackClick / trackEvent without
 * importing the full analytics module (which transitively pulls in
 * sessionTracker, routeContext, GA4 helpers, ~30-80KB).
 *
 * Calls are queued until App.tsx finishes its deferToIdle dynamic-load of
 * the real analytics module, then drained in order. Subsequent calls
 * bypass the queue and call the real module directly.
 *
 * Use this in eager-render path components (HeroShell, Navbar) so analytics
 * stays out of the main bundle. Code paths that are already lazy-loaded
 * (dashboard, platform) can keep importing from @/shared/utils/analytics directly.
 */

type Pending =
  | { fn: 'click'; args: [string, string, Record<string, unknown>?] }
  | { fn: 'event'; args: [string, Record<string, unknown>?] };

const _queue: Pending[] = [];
let _real: {
  trackClick: (buttonText: string, section: string, metadata?: Record<string, unknown>) => void;
  trackEvent: (eventName: string, data?: Record<string, unknown>) => void;
} | null = null;

export function trackClick(buttonText: string, section: string, metadata: Record<string, unknown> = {}): void {
  if (_real) {
    _real.trackClick(buttonText, section, metadata);
  } else {
    _queue.push({ fn: 'click', args: [buttonText, section, metadata] });
  }
}

export function trackEvent(eventName: string, data: Record<string, unknown> = {}): void {
  if (_real) {
    _real.trackEvent(eventName, data);
  } else {
    _queue.push({ fn: 'event', args: [eventName, data] });
  }
}

// Called by App.tsx after dynamic-importing @/utils/analytics.
export function _bindAnalytics(mod: typeof _real): void {
  _real = mod;
  if (!mod) return;
  for (const item of _queue) {
    if (item.fn === 'click') mod.trackClick(...item.args);
    else mod.trackEvent(...item.args);
  }
  _queue.length = 0;
}
